import { Fragment, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getFamilyMemberFullName } from '@/features/families/mappers';
import { formatDate } from '@/lib/format';
import type { FamilyViewModel } from '@/models/family';

interface FamilyIdentityBandProps {
  readonly family: FamilyViewModel;
  /** Slot for badges rendered before the action area. */
  readonly statusSlot?: ReactNode;
  /** Slot for actions on the right (e.g. Export, future bulk actions). */
  readonly actionSlot?: ReactNode;
}

/**
 * Family case header. Same three-tier hierarchy as `PatientIdentityBand`:
 * name + inline status pill → definition-list metadata row → right-rail
 * status / action slots. Stays visually parallel to the patient page so
 * the two records feel like siblings.
 */
export function FamilyIdentityBand({
  family,
  statusSlot,
  actionSlot,
}: FamilyIdentityBandProps): ReactElement {
  const { t } = useTranslation();
  const displayName =
    family.name.length > 0 ? family.name : t('routes.family.unnamedFamily');

  return (
    <header className="bg-card border-b border-border">
      <div className="px-8 py-3 flex items-center gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            <h1 className="text-[18px] font-semibold leading-none tracking-tight truncate text-foreground">
              {displayName}
            </h1>
            <span className="inline-flex items-center h-[20px] px-2 rounded-full bg-muted text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {t('routes.family.header.familyBadge')}
            </span>
          </div>
          <MetaRow family={family} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {statusSlot}
          {actionSlot}
        </div>
      </div>
    </header>
  );
}

function MetaRow({ family }: { readonly family: FamilyViewModel }): ReactElement | null {
  const { t } = useTranslation();
  const items: ReactNode[] = [];

  items.push(
    <span key="members" className="text-foreground/90 font-medium tabular-nums">
      {t('routes.family.header.members', { count: family.members.length })}
    </span>,
  );

  // PTV2-1313: the Family ID is a raw guid — not user-friendly, so it is no
  // longer shown in the header. `family.guid` is still used everywhere else
  // (routing, query keys, mutations); only this display was removed.

  if (family.probandGuid !== null) {
    const probandMember =
      family.members.find((m) => m.cascadeRole === 'PROBAND') ?? null;
    const probandName =
      probandMember !== null ? getFamilyMemberFullName(probandMember) : null;
    items.push(
      <MetaPair
        key="proband"
        label={t('routes.family.header.probandLabel')}
        value={
          <Link
            to={`/patients/${family.probandGuid}`}
            className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            {probandName ?? t('routes.family.header.probandView')}
          </Link>
        }
      />,
    );
  }

  if (family.updatedAt !== null) {
    items.push(
      <MetaPair
        key="updated"
        label={t('routes.family.header.updatedLabel')}
        value={<span className="tabular-nums">{formatDate(family.updatedAt)}</span>}
      />,
    );
  }

  return (
    <dl className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-foreground/80">
      {items.map((item, idx) => (
        <Fragment key={idx}>
          {idx > 0 && (
            <span aria-hidden="true" className="text-border/80 select-none">
              ·
            </span>
          )}
          {item}
        </Fragment>
      ))}
    </dl>
  );
}

interface MetaPairProps {
  readonly label: string;
  readonly value: ReactNode;
}

function MetaPair({ label, value }: MetaPairProps): ReactElement {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </dt>
      <dd className="text-foreground/85">{value}</dd>
    </span>
  );
}
